import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Camera,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  Info,
  X,
  Check,
  ImageOff
} from 'lucide-react';
import { api } from '../../services/api';
import { Toast } from '../ui/Toast';
import ChampSelect from '../ui/ChampSelect';
import EnTeteParametrage from './EnTeteParametrage';
import EnTeteSection from './EnTeteSection';
import CarteReglages from './CarteReglages';
import ChargementSection from './ChargementSection';

/**
 * ============================================================================
 * PHOTOS DES ÉLÈVES — AVANCEMENT DE LA CAMPAGNE
 * ============================================================================
 *
 * Savoir si un élève a une photo dans son dossier demandait d'ouvrir ce
 * dossier. À 789 élèves pour la plus grande des écoles, la question « qui n'a
 * pas de photo » n'avait donc pas de réponse praticable. Cet écran la lit d'un
 * seul coup, par classe, et sort la feuille que l'enseignant emporte.
 *
 * ----------------------------------------------------------------------------
 * ⚠️ CET ÉCRAN EST CONSTRUIT AUTOUR DU MANQUE, PAS AUTOUR DES PHOTOS
 * ----------------------------------------------------------------------------
 * Mesure du 4 octobre 2026 sur la production : 2 098 élèves actifs sur les
 * trois écoles réelles, et TROIS photos en tout (csap1 1/789, csap2 1/495,
 * gestion_scolaire 1/814). Zéro pour cent partout.
 *
 * Une liste « qui a une photo » ferait donc UNE LIGNE. Ce qui compte est ce
 * qui reste à faire et par quelle classe commencer — d'où l'ordre de l'écran :
 * avancement de l'école, puis une ligne par classe triée par le nombre de
 * manques, puis les élèves. Ne pas réorganiser cet écran autour des photos
 * obtenues : ce serait vrai le jour où la campagne sera finie, et faux tous
 * les jours où elle sert.
 *
 * ----------------------------------------------------------------------------
 * ⚠️ LISIBLE À ZÉRO — ce n'est pas un cas limite, c'est le cas réel
 * ----------------------------------------------------------------------------
 * Trois décisions en découlent, et elles sont délibérées :
 *
 * 1. LA BARRE DE PROGRESSION N'EXISTE QUE S'IL Y A QUELQUE CHOSE DEDANS.
 *    À zéro photo, aucune barre n'est rendue — on écrit « Aucune photo ».
 *    Quarante barres vides alignées se lisent comme un écran en panne, pas
 *    comme une campagne qui démarre.
 * 2. LE POURCENTAGE NE S'AFFICHE QUE S'IL DIT QUELQUE CHOSE. « 0 % » répété
 *    sur quarante lignes n'apprend rien ; le chiffre qui sert au départ est le
 *    NOMBRE D'ÉLÈVES À PHOTOGRAPHIER, et c'est lui qui est en gras.
 * 3. LE MANQUE EST EN GRIS NEUTRE, PAS EN AMBRE NI EN ROUGE. À 100 % de
 *    manques, une couleur d'alerte peindrait l'écran entier et se lirait comme
 *    une anomalie du logiciel. L'accent `#246990` est réservé au PROGRÈS :
 *    chaque tache de bleu qui apparaît est une avancée. Ne pas inverser.
 *
 * ----------------------------------------------------------------------------
 * ⚠️ LA DISPOSITION A ÉTÉ REPRISE LE 4 OCTOBRE 2026, EN RECETTE
 * ----------------------------------------------------------------------------
 * Ce que Daniel a relevé à l'écran, et qui est maintenant fixé ici — ce sont
 * des règles, pas des préférences, et les quatre premières sont la norme
 * écrite (PARAMETRAGE-MODULES.md) :
 *
 * 1. UN SEUL EN-TÊTE DE PAGE DANS LE FICHIER. L'écran de chargement en avait
 *    sa propre copie, avec son propre sous-titre — qui avait déjà divergé :
 *    celui du chargement ne nommait PAS l'année scolaire. C'est exactement la
 *    dérive que §1 décrit. L'en-tête est rendu une fois ; seul le CONTENU du
 *    panneau bascule entre le disque et les cartes, et la page ne saute plus
 *    au moment où les données arrivent.
 * 2. LA BARRE DE FILTRES VIT DANS LA CARTE QU'ELLE FILTRE. Posée nue entre
 *    deux cartes, elle se lisait comme appartenant à celle du dessus — donc
 *    comme un filtre des classes, qu'elle n'est pas. Elle est désormais la
 *    première ligne de la carte « Élèves », au-dessus de la liste qu'elle
 *    réduit, séparée par le `divide-y` de la carte.
 * 3. L'ENCADRÉ D'INFORMATION EST EN FIN DE PAGE. Entre le total de l'école et
 *    le tableau des classes, il repoussait les deux chiffres qu'on vient
 *    chercher sous la ligne de flottaison. Il explique l'écran (§8), il ne
 *    porte aucune donnée : il se lit après, et sa place est juste sous le
 *    tableau dont il commente la colonne « Origine ».
 * 4. LES LIGNES DE CLASSE SONT SUR UNE GRILLE. En `flex flex-wrap`, les
 *    colonnes se décalaient d'une ligne à l'autre et le bouton PDF tombait
 *    seul sur sa ligne aux largeurs intermédiaires. Sur quarante lignes toutes
 *    identiques — l'état réel — c'est précisément ce qui donnait l'impression
 *    d'un écran mal rangé.
 * 5. ⚠️ LA LISTE DÉROULANTE EST `ChampSelect`, JAMAIS UN `<select>` NATIF.
 *    Un natif ne prend ni la hauteur `h-9`, ni le rayon, ni la couleur de
 *    focus, et son menu est dessiné par le système d'exploitation : il ne
 *    ressemble à aucun autre écran, et il ne ressemble pas au même sur deux
 *    postes. Le champ de recherche à côté suit la même règle de hauteur
 *    (DESIGN-SYSTEM.md § Champs de saisie) — sans quoi les deux contrôles de
 *    la même barre ne sont pas alignés, ce qui se voit même sans le chercher.
 *
 * ----------------------------------------------------------------------------
 * ⚠️ CETTE PAGE NE MODIFIE RIEN
 * ----------------------------------------------------------------------------
 * Aucun des deux régimes d'enregistrement de PARAMETRAGE-MODULES.md §5 : pas
 * de formulaire, pas d'envoi de photo, pas une seule écriture. Une photo se
 * pose depuis la fiche de l'élève, ou arrive de l'application des parents.
 *
 * ET AUCUNE LIGNE N'EST CLIQUABLE. Le bouton « Ouvrir la fiche » a été retiré
 * de l'écran des badges le 3 octobre 2026 à la demande de Daniel : NE PAS le
 * réintroduire ici sous une autre forme — ni bouton par ligne, ni ligne
 * cliquable, ni lien vers un module. C'est pourquoi ce fichier n'importe pas
 * `useNavigate` : l'absence est volontaire et doit se voir.
 *
 * ----------------------------------------------------------------------------
 * L'ORIGINE DE LA PHOTO est l'autre information que cet écran existe pour dire
 * ----------------------------------------------------------------------------
 * Jusqu'au 4 octobre 2026, rien ne distinguait une photo posée par l'école
 * d'une photo envoyée par une famille depuis l'application mobile. Les
 * colonnes `photo_source` / `photo_maj_le` / `photo_par` sont arrivées par
 * migration ce jour-là.
 *
 * Une origine INCONNUE n'est donc PAS une anomalie : c'est une photo
 * antérieure au suivi. Elle se lit discrètement, en gris, et surtout jamais
 * avec une icône d'alerte — sans quoi les quelques photos existantes
 * passeraient pour des erreurs.
 *
 * La norme de l'écran : PARAMETRAGE-MODULES.md (§1 coquille, §3 en-tête de
 * section, §4 carte, §6 chargement/vide/échec, §8 encadré). Un seul sujet,
 * donc pas d'onglets (§2) et l'accent reste la couleur principale. Le panneau
 * blanc qui entoure les cartes est celui que §2 demande à une page sans
 * onglets, et c'est la disposition de la page de référence
 * (`ParametrageCarnetEvaluation.jsx`) : cartes DANS le panneau, un seul cadre.
 *
 * ----------------------------------------------------------------------------
 * Contrastes mesurés (WCAG 2.1, ratios calculés, pas estimés)
 * ----------------------------------------------------------------------------
 *   #246990 sur #ffffff .................. 6,00:1   AA
 *   #ffffff sur #246990 .................. 6,00:1   AA   (boutons pleins)
 *   #246990 sur #eef7fb .................. 5,53:1   AA   (encadré, badge École)
 *   #1f2937 sur #ffffff .................. 14,68:1  AAA  (chiffre du manque)
 *   #374151 sur #ffffff .................. 10,31:1  AAA  (valeurs de tableau)
 *   #4b5563 sur #ffffff .................. 7,56:1   AAA  (« Sans photo »)
 *   #4b5563 sur #f3f4f6 .................. 6,87:1   AA   (badge Sans photo)
 *   #6b7280 sur #ffffff .................. 4,83:1   AA   (texte discret)
 *   #6b7280 sur #f9fafb .................. 4,63:1   AA   (ligne survolée)
 *   #0f766e sur #f0fdfa .................. 5,25:1   AA   (badge Famille)
 *   #b91c1c sur #fef2f2 .................. 5,91:1   AA   (bandeau d'échec)
 *
 * ⚠️ DEUX COULEURS ÉCARTÉES SUR MESURE, ne pas les réintroduire :
 *   - `text-gray-400` (#9ca3af) sur blanc = 2,54:1 — ÉCHEC. C'est la teinte
 *     employée ailleurs dans le dépôt pour les mentions discrètes ; elle est
 *     remplacée ici par `text-gray-500` (4,83:1). « Discret » ne veut pas dire
 *     illisible, et l'origine inconnue doit se lire.
 *   - Le teal maison #319190 sur #f0fdfa = 3,61:1 — insuffisant pour du texte
 *     de cette taille. Le badge « Famille » emploie donc teal-700 #0f766e.
 */

const ACCENT = '#246990';
const ACCENT_SOMBRE = '#1a4f6e';

/**
 * Le nombre de lignes d'élèves rendues d'emblée.
 *
 * La plus grande école compte 814 élèves et aucun n'a de photo : la liste
 * complète est donc, aujourd'hui, 814 lignes identiques. Le plafond garde la
 * page réactive et la recherche vive ; le décompte exact reste affiché, et un
 * bouton lève le plafond pour qui veut tout voir.
 *
 * Ce n'est pas une pagination : filtrer par classe ramène toujours une classe
 * entière (60 élèves au plus dans les données réelles), donc sous le plafond.
 */
const PLAFOND_LIGNES = 150;

/**
 * Les colonnes d'une ligne de classe.
 *
 * En grille et non en `flex-wrap` : quarante lignes dont les colonnes ne
 * tombent pas au même endroit se lisent comme un tableau cassé, et c'est ce
 * que la recette du 4 octobre a relevé. Les largeurs sont figées à partir de
 * `sm` ; en dessous, la ligne s'empile en une colonne, où une grille à quatre
 * colonnes serait illisible.
 */
const GRILLE_CLASSE =
  'grid grid-cols-1 items-center gap-x-4 gap-y-2.5 px-5 py-4 transition-colors ' +
  'hover:bg-gray-50/70 sm:grid-cols-[minmax(0,1fr)_minmax(0,220px)_130px_auto]';

/**
 * Le pourcentage d'avancement, sans mentir dans les deux sens.
 *
 * Un arrondi nu produit deux affirmations fausses, et les deux comptent sur
 * une campagne qu'on suit au dossier près :
 *   - 599 photos sur 600 donnent « 100 % » alors qu'il manque un élève ;
 *   - 1 photo sur 789 donne « 0 % » alors que la campagne a commencé.
 * D'où le plafond à 99 tant qu'un dossier reste vide, et le plancher à 1 dès
 * qu'une photo existe. 100 % ne s'écrit que si le compte est exact.
 */
const pourcentageHonnete = (avecPhoto, total) => {
  if (!total) return 0;
  if (avecPhoto >= total) return 100;
  if (avecPhoto <= 0) return 0;
  return Math.min(99, Math.max(1, Math.round((avecPhoto / total) * 100)));
};

/**
 * La date de mise à jour de la photo, en JJ/MM/AAAA.
 *
 * ⚠️ La conversion se fait par DÉCOUPAGE DE LA CHAÎNE, jamais par
 * `new Date('2026-10-04')` : la norme impose de lire une date sans heure comme
 * de l'UTC, et un poste à l'ouest de Greenwich afficherait la veille. Cette
 * facture a déjà été payée trois fois sur ce chantier.
 *
 * Le serveur envoie « AAAA-MM-JJ HH:MM » — une forme qui se trie et se
 * compare, et c'est pour cela qu'elle ne change pas côté serveur.
 */
const DateMiseAJour = ({ valeur }) => {
  if (!valeur) {
    return <span className="text-xs text-gray-500">Non suivie</span>;
  }

  const [datePart, heure] = String(valeur).split(' ');
  const [annee, mois, jour] = String(datePart).split('-');
  const dateFr = annee && mois && jour ? `${jour}/${mois}/${annee}` : datePart;

  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-mono text-[13px] text-gray-700">{dateFr}</span>
      {heure && <span className="font-mono text-[11px] text-gray-500">{heure}</span>}
    </span>
  );
};

/**
 * D'où vient la photo : de l'école, d'une famille, ou d'avant le suivi.
 *
 * Les trois cas se distinguent, et le troisième reste discret : une origine
 * inconnue est une photo antérieure au 4 octobre 2026, pas un défaut. Pas
 * d'icône d'alerte dessus — ce serait traiter les seules photos existantes
 * comme des anomalies.
 */
const OriginePhoto = ({ aPhoto, source }) => {
  if (!aPhoto) {
    // Pas de photo, donc pas d'origine. Un tiret, et son équivalent parlé pour
    // les lecteurs d'écran : « — » seul ne s'énonce pas.
    return (
      <span className="text-xs text-gray-500">
        <span aria-hidden="true">—</span>
        <span className="sr-only">Sans objet, aucune photo</span>
      </span>
    );
  }

  if (source === 'ecole') {
    return (
      <span
        className="inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold"
        style={{ color: ACCENT, backgroundColor: '#eef7fb', borderColor: 'rgba(36,105,144,0.2)' }}
      >
        École
      </span>
    );
  }

  if (source === 'famille') {
    return (
      <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700">
        Famille
      </span>
    );
  }

  // Toute autre valeur, y compris `null` : la photo existe, son origine n'a
  // pas été enregistrée. `photo_source` est un VARCHAR et non une ENUM — une
  // valeur inconnue doit s'afficher telle quelle plutôt que de disparaître
  // derrière un libellé inventé.
  return (
    <span className="text-xs text-gray-500" title="Photo antérieure au suivi de l'origine">
      {source ? String(source) : 'Origine inconnue'}
    </span>
  );
};

/**
 * L'état d'une photo, pour un élève.
 *
 * L'absence s'écrit EN MOTS. Une case vide se lit comme une donnée qu'on n'a
 * pas chargée, pas comme une photo qui manque — et ici, presque tout manque.
 */
const EtatPhoto = ({ aPhoto }) =>
  aPhoto ? (
    <span
      className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: ACCENT, backgroundColor: '#eef7fb', borderColor: 'rgba(36,105,144,0.2)' }}
    >
      <Check size={11} className="flex-shrink-0" />
      Photo
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
      <ImageOff size={11} className="flex-shrink-0" />
      Sans photo
    </span>
  );

/**
 * L'avancement d'une classe.
 *
 * ⚠️ AUCUNE BARRE N'EST RENDUE À ZÉRO PHOTO. C'est la règle de lisibilité à
 * zéro : une barre vide ressemble à une panne, et c'est aujourd'hui l'état de
 * toutes les classes de la production. La barre apparaît le jour où elle a
 * quelque chose à montrer, et son apparition est elle-même l'information.
 */
const AvancementClasse = ({ total, avecPhoto }) => {
  if (!total) {
    return <span className="text-xs text-gray-500">Aucun élève inscrit</span>;
  }

  if (avecPhoto <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600">
        <ImageOff size={13} className="flex-shrink-0 text-gray-500" />
        Aucune photo
      </span>
    );
  }

  const pourcentage = pourcentageHonnete(avecPhoto, total);

  // `flex-wrap` : la colonne de la grille fait 220 px au plus, et une classe
  // à trois chiffres (« 123 sur 456 ») y déborderait sans ce repli.
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      <div className="h-1.5 w-24 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full"
          style={{ width: `${pourcentage}%`, backgroundColor: ACCENT }}
        />
      </div>
      <span className="text-xs font-semibold" style={{ color: ACCENT }}>
        {avecPhoto} sur {total}
      </span>
      <span className="text-[11px] text-gray-500">{pourcentage} %</span>
    </div>
  );
};

const PhotosEleves = ({ onBackToDashboard }) => {
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [totaux, setTotaux] = useState({ eleves: 0, avec_photo: 0, sans_photo: 0 });
  const [classes, setClasses] = useState([]);
  const [eleves, setEleves] = useState([]);
  // ⚠️ L'ANNÉE SUR LAQUELLE ON COMPTE, ÉCRITE EN CLAIR. Le serveur compte les
  // élèves actifs d'UNE année : celle choisie à la connexion, sinon l'année
  // active. Relevé du 04/10/2026 sur csap1 : 786 élèves actifs sur 2025-2026,
  // et 3 seulement sur l'année active 2026-2027, le temps des réinscriptions.
  // « 3 élèves actifs » sans son année se lit comme une base vide — ne pas
  // retirer ce libellé de l'en-tête.
  const [anneeScolaire, setAnneeScolaire] = useState(null);

  const [recherche, setRecherche] = useState('');
  const [classeFiltre, setClasseFiltre] = useState('toutes');
  const [toutAfficher, setToutAfficher] = useState(false);

  // Quel export tourne : 'tout', ou l'identifiant de la classe. Un seul état
  // pour tous les boutons — sans quoi cliquer l'export d'une classe ferait
  // tourner le disque de toutes les lignes.
  const [exportEnCours, setExportEnCours] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await api.eleves.getCampagnePhotos();
      if (reponse?.success) {
        setAnneeScolaire(reponse.annee_scolaire || null);
        const lus = reponse.totaux || {};
        setTotaux({
          eleves: Number(lus.eleves) || 0,
          avec_photo: Number(lus.avec_photo) || 0,
          sans_photo: Number(lus.sans_photo) || 0
        });
        // L'ordre des classes est celui du serveur : le plus de manques
        // d'abord. On ne retrie PAS ici — deux tris concurrents finissent
        // toujours par diverger, et c'est le serveur qui connaît la règle.
        setClasses(Array.isArray(reponse.classes) ? reponse.classes : []);
        setEleves(Array.isArray(reponse.eleves) ? reponse.eleves : []);
      } else {
        // Une lecture qui échoue ne s'affiche JAMAIS comme une campagne à zéro
        // (PARAMETRAGE-MODULES.md §6). Ici le piège est redoutable : « aucune
        // photo » est précisément l'état réel attendu, donc un échec muet
        // passerait pour une donnée juste et lancerait une campagne sur des
        // chiffres faux.
        setAnneeScolaire(null);
        setTotaux({ eleves: 0, avec_photo: 0, sans_photo: 0 });
        setClasses([]);
        setEleves([]);
        setErreur(reponse?.message || reponse?.error || 'Chargement impossible');
      }
    } catch (e) {
      setAnneeScolaire(null);
      setTotaux({ eleves: 0, avec_photo: 0, sans_photo: 0 });
      setClasses([]);
      setEleves([]);
      setErreur(e.message || 'Chargement impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  // La recherche porte sur le nom, le prénom ET le matricule : on cherche
  // aussi bien « où est Mabiala » que le matricule lu sur une fiche papier.
  const listeEleves = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return eleves.filter((eleve) => {
      if (classeFiltre !== 'toutes' && String(eleve.classe_id) !== String(classeFiltre)) {
        return false;
      }
      if (!terme) return true;
      return [eleve.nom, eleve.prenom, eleve.matricule].some((champ) =>
        String(champ || '').toLowerCase().includes(terme)
      );
    });
  }, [eleves, recherche, classeFiltre]);

  const listeAffichee = useMemo(
    () => (toutAfficher ? listeEleves : listeEleves.slice(0, PLAFOND_LIGNES)),
    [listeEleves, toutAfficher]
  );

  /**
   * Les entrées de la liste déroulante des classes.
   *
   * Même source et même ordre que les lignes de la carte « Avancement par
   * classe » — celui du serveur, le plus de manques d'abord : la classe par
   * laquelle on commence la campagne est donc la première du menu.
   *
   * `valeur` reste `classe_id` tel que le serveur l'a envoyé ; `ChampSelect`
   * le convertit en chaîne, comme le filtre ci-dessus qui compare des chaînes.
   */
  const optionsClasses = useMemo(
    () => [
      { valeur: 'toutes', libelle: 'Toutes les classes' },
      ...classes.map((classe) => ({
        valeur: classe.classe_id,
        libelle: `${classe.classe || 'Classe non renseignée'}${
          Number(classe.sans_photo) > 0 ? ` — ${classe.sans_photo} sans photo` : ' — complète'
        }`
      }))
    ],
    [classes]
  );

  // La répartition par origine : c'est la question posée au départ — qui a
  // posé la photo, l'école ou la famille. Calculée sur l'école entière et non
  // sur la vue filtrée : c'est un état des lieux, pas un décompte de filtre.
  const origines = useMemo(() => {
    let ecole = 0;
    let famille = 0;
    let inconnue = 0;
    for (const eleve of eleves) {
      if (!eleve.a_photo) continue;
      if (eleve.source === 'ecole') ecole += 1;
      else if (eleve.source === 'famille') famille += 1;
      else inconnue += 1;
    }
    return { ecole, famille, inconnue };
  }, [eleves]);

  const classesAFaire = useMemo(
    () => classes.filter((classe) => Number(classe.sans_photo) > 0).length,
    [classes]
  );
  const classesCompletes = useMemo(
    () =>
      classes.filter(
        (classe) => Number(classe.total) > 0 && Number(classe.sans_photo) === 0
      ).length,
    [classes]
  );

  const campagneCommencee = totaux.avec_photo > 0;

  // Le sous-titre nomme l'année dès que le serveur l'a dite. Pas de secours
  // calculé ici : une année devinée côté écran finirait par contredire celle
  // sur laquelle le serveur a compté, et c'est exactement ce qu'on cherche à
  // éviter.
  const sousTitrePage = anneeScolaire
    ? `Année scolaire ${anneeScolaire} — avancement par classe, origine des photos et élèves encore sans photo`
    : 'Avancement par classe, origine des photos et élèves encore sans photo';

  // ⚠️ L'ANNÉE VOYAGE AVEC LE CHIFFRE, et pas seulement dans le sous-titre de
  // la page. « 3 élèves actifs » écrit seul dans la pastille d'une carte se lit
  // comme une base vide ; c'est le cas réel de csap1 avant les réinscriptions,
  // et le sous-titre est trois niveaux de titre plus haut.
  const nEleves = totaux.eleves;
  const compteurEcole = `${nEleves} élève${nEleves > 1 ? 's' : ''} actif${
    nEleves > 1 ? 's' : ''
  }${anneeScolaire ? ` · ${anneeScolaire}` : ''}`;

  const exporter = async (classe) => {
    const cle = classe ? classe.classe_id : 'tout';
    setExportEnCours(cle);
    const resultat = await api.eleves.downloadCampagnePhotosPDF(classe ? classe.classe_id : null);
    setExportEnCours(null);
    setToast({
      show: true,
      message: resultat?.success
        ? classe
          ? `Liste de ${classe.classe} exportée.`
          : 'Liste de toutes les classes exportée.'
        : `Export impossible : ${resultat?.error || 'erreur inconnue'}`,
      type: resultat?.success ? 'success' : 'error'
    });
  };

  // Changer de classe remet le plafond : une classe entière tient sous la
  // limite, et laisser « tout afficher » actif ferait rendre 800 lignes au
  // retour sur « Toutes ».
  const changerClasse = (valeur) => {
    setClasseFiltre(valeur);
    setToutAfficher(false);
  };

  const exportBloque = exportEnCours !== null || totaux.eleves === 0;

  return (
    <div className="w-full">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />

      {/* ⚠️ UN SEUL EN-TÊTE DE PAGE DANS CE FICHIER (norme §1). L'écran de
          chargement n'a pas le sien : c'est la duplication que la norme nomme,
          et les deux copies avaient déjà divergé sur le sous-titre. Seul le
          contenu du panneau bascule ci-dessous. */}
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        titre="Photos des élèves"
        sousTitre={sousTitrePage}
        actions={
          <button
            type="button"
            onClick={() => exporter(null)}
            disabled={exportBloque}
            className="flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
            style={exportBloque ? undefined : { backgroundColor: ACCENT }}
          >
            {exportEnCours === 'tout' ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Download size={13} />
            )}
            Exporter toutes les classes
          </button>
        }
      />

      {/* Le panneau blanc unique d'une page sans onglets (norme §2), tel que la
          page de référence le pose. Le disque de chargement est DEDANS : rendu
          au-dessus, il faisait sauter toute la page à l'arrivée des données. */}
      <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
        {chargement ? (
          <ChargementSection accent={ACCENT} libelle="Lecture des dossiers élèves…" />
        ) : (
          <div className="space-y-5">
            <EnTeteSection
              icone={Camera}
              titre="Campagne photo"
              sousTitre="Qui a une photo dans son dossier, d'où elle vient, et combien d'élèves restent à photographier"
              accent={ACCENT}
              accentSombre={ACCENT_SOMBRE}
            />

            {/* Échec de lecture : bandeau dans la zone concernée, pas un toast —
                un toast disparaît et laisse un écran à zéro sans explication,
                or « zéro photo » est ici un résultat plausible
                (PARAMETRAGE-MODULES.md §6). */}
            {erreur && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
                  <AlertTriangle size={15} className="text-red-600" />
                </div>
                <p className="text-xs font-semibold text-red-700">
                  Les dossiers n&apos;ont pas pu être lus : {erreur}
                </p>
                <button
                  type="button"
                  onClick={charger}
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white transition-all hover:bg-red-700"
                >
                  <RefreshCw size={13} /> Réessayer
                </button>
              </div>
            )}

            {/* ----------------------------------------------------------------
                L'AVANCEMENT DE L'ÉCOLE
                ----------------------------------------------------------------
                Deux formes, et le choix entre elles est la décision de
                lisibilité à zéro. Campagne non commencée : on écrit le travail
                à faire, en clair, sans jauge ni pourcentage. Campagne commencée :
                la jauge et la répartition par origine prennent leur sens. */}
            {!erreur && totaux.eleves > 0 && (
              <CarteReglages titre="Avancement de l'école" accent={ACCENT} compteur={compteurEcole}>
                {campagneCommencee ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                      <div>
                        <p className="text-2xl font-bold" style={{ color: ACCENT }}>
                          {totaux.avec_photo}
                          <span className="ml-1.5 text-sm font-semibold text-gray-600">
                            sur {totaux.eleves}
                          </span>
                        </p>
                        <p className="mt-0.5 text-[11px] text-gray-500">dossiers avec photo</p>
                      </div>

                      <div>
                        <p className="text-2xl font-bold text-gray-800">{totaux.sans_photo}</p>
                        <p className="mt-0.5 text-[11px] text-gray-500">restent à photographier</p>
                      </div>

                      <div className="min-w-[180px] flex-1">
                        <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pourcentageHonnete(totaux.avec_photo, totaux.eleves)}%`,
                              backgroundColor: ACCENT
                            }}
                          />
                        </div>
                        <p className="mt-1.5 text-[11px] font-semibold text-gray-600">
                          {pourcentageHonnete(totaux.avec_photo, totaux.eleves)} % des dossiers
                          complétés
                        </p>
                      </div>
                    </div>

                    {/* La répartition par origine : la question d'origine de cet
                        écran. Elle n'apparaît que s'il y a des photos — à zéro,
                        trois compteurs à zéro n'apprennent rien. */}
                    <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Origine des photos
                      </span>
                      <span
                        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold"
                        style={{
                          color: ACCENT,
                          backgroundColor: '#eef7fb',
                          borderColor: 'rgba(36,105,144,0.2)'
                        }}
                      >
                        {origines.ecole} posée{origines.ecole > 1 ? 's' : ''} par l&apos;école
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700">
                        {origines.famille} envoyée{origines.famille > 1 ? 's' : ''} par une famille
                      </span>
                      {origines.inconnue > 0 && (
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-500">
                          {origines.inconnue} d&apos;origine inconnue
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  // DÉPART À ZÉRO. Pas de jauge, pas de « 0 % » : le seul chiffre
                  // qui sert est le nombre de dossiers à remplir, et la phrase
                  // dit que c'est un début, pas une panne.
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
                        <ImageOff className="h-5 w-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-800">{totaux.sans_photo}</p>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          dossiers d&apos;élèves sans photo, sur {totaux.eleves}
                        </p>
                      </div>
                    </div>

                    <div className="max-w-xl text-xs leading-relaxed text-gray-600">
                      <p className="mb-1 text-[13px] font-semibold text-gray-700">
                        La campagne photo n&apos;a pas encore commencé
                      </p>
                      <p>
                        Aucun dossier ne porte de photo pour l&apos;instant. Les{' '}
                        {classesAFaire} classe{classesAFaire > 1 ? 's' : ''} ci-dessous sont donc
                        toutes à faire : commencez par celle qui vous arrange, exportez sa liste,
                        et l&apos;avancement apparaîtra ici dès la première photo enregistrée.
                      </p>
                    </div>
                  </div>
                )}
              </CarteReglages>
            )}

            {/* ----------------------------------------------------------------
                UNE LIGNE PAR CLASSE
                ----------------------------------------------------------------
                Triée par le nombre de manques (ordre du serveur). Le chiffre en
                gras est le NOMBRE À PHOTOGRAPHIER : c'est lui qui sert à décider
                par où commencer et combien de temps y passer. L'export par ligne
                est la feuille que l'enseignant emporte. */}
            {!erreur && classes.length > 0 && (
              <CarteReglages
                titre="Avancement par classe"
                accent={ACCENT}
                compteur={
                  classesCompletes > 0
                    ? `${classesAFaire} à faire · ${classesCompletes} complète${classesCompletes > 1 ? 's' : ''}`
                    : `${classes.length} classe${classes.length > 1 ? 's' : ''}`
                }
                dense
              >
                {classes.map((classe) => {
                  const total = Number(classe.total) || 0;
                  const avecPhoto = Number(classe.avec_photo) || 0;
                  const sansPhoto = Number(classe.sans_photo) || 0;
                  const complete = total > 0 && sansPhoto === 0;

                  return (
                    <div key={classe.classe_id ?? classe.classe} className={GRILLE_CLASSE}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: complete ? ACCENT : '#9ca3af' }}
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-800">
                            {classe.classe || 'Classe non renseignée'}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {total} élève{total > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      <div className="min-w-0">
                        <AvancementClasse total={total} avecPhoto={avecPhoto} />
                      </div>

                      {/* Le chiffre qui pilote la campagne. En gris neutre et non
                          en ambre : à 100 % de manques, l'ambre peindrait l'écran
                          entier et se lirait comme une alerte du logiciel. */}
                      <div className="sm:text-right">
                        {complete ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-semibold"
                            style={{ color: ACCENT }}
                          >
                            <Check size={13} className="flex-shrink-0" />
                            Classe complète
                          </span>
                        ) : (
                          <>
                            <p className="text-[15px] font-bold text-gray-800">{sansPhoto}</p>
                            <p className="text-[11px] text-gray-500">à photographier</p>
                          </>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => exporter(classe)}
                        disabled={exportEnCours !== null || total === 0}
                        aria-label={`Exporter la liste de ${classe.classe || 'la classe'} en PDF`}
                        className="flex items-center gap-1.5 justify-self-start rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:justify-self-end"
                      >
                        {exportEnCours === classe.classe_id ? (
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                        ) : (
                          <Download size={13} />
                        )}
                        PDF
                      </button>
                    </div>
                  );
                })}
              </CarteReglages>
            )}

            {/* ----------------------------------------------------------------
                LES ÉLÈVES
                ----------------------------------------------------------------
                La barre de filtres est la PREMIÈRE LIGNE DE CETTE CARTE, et non
                un bloc posé au-dessus : elle réduit cette liste-ci, et flottant
                entre deux cartes elle se lisait comme un filtre des classes. */}
            {!erreur && totaux.eleves > 0 && (
              <CarteReglages
                titre="Élèves"
                accent={ACCENT}
                compteur={
                  recherche.trim() || classeFiltre !== 'toutes'
                    ? `${listeEleves.length} sur ${totaux.eleves}`
                    : `${totaux.eleves} élève${totaux.eleves > 1 ? 's' : ''}`
                }
                dense
              >
                <div className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                  <div className="relative min-w-[240px] flex-1">
                    <Search
                      size={15}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                    />
                    <input
                      type="text"
                      value={recherche}
                      onChange={(e) => setRecherche(e.target.value)}
                      placeholder="Rechercher un nom ou un matricule…"
                      aria-label="Rechercher un élève par nom ou matricule"
                      className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-9 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                    />
                    {recherche && (
                      <button
                        type="button"
                        onClick={() => setRecherche('')}
                        aria-label="Effacer la recherche"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 transition-colors hover:text-gray-700"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* ⚠️ `ChampSelect`, la liste déroulante de la charte, et non
                      un `<select>` natif : voir la règle 5 en tête de fichier.
                      Pas une rangée de puces non plus — quarante classes en
                      puces occuperaient plus de place que la liste filtrée. */}
                  <div className="w-full sm:w-[260px]">
                    <ChampSelect
                      id="photos-eleves-filtre-classe"
                      valeur={classeFiltre}
                      onChange={changerClasse}
                      options={optionsClasses}
                      placeholder="Toutes les classes"
                    />
                  </div>
                </div>

                {listeEleves.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
                    <Search className="h-5 w-5 text-gray-500" />
                    <p className="text-sm font-semibold text-gray-700">
                      Aucun élève ne correspond à cette recherche
                    </p>
                    <p className="text-xs text-gray-500">
                      La recherche porte sur le nom, le prénom et le matricule.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-gray-100 bg-gray-50/60">
                            <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                              Élève
                            </th>
                            <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                              Classe
                            </th>
                            <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                              Photo
                            </th>
                            {/* Les deux colonnes d'origine n'existent que si
                                l'école a au moins une photo. À zéro — l'état
                                réel d'aujourd'hui — ce seraient 800 lignes de
                                tirets, et c'est exactement le bruit que cet
                                écran doit éviter. Le critère est le total de
                                L'ÉCOLE et non celui de la vue filtrée : sinon
                                les colonnes apparaîtraient et disparaîtraient
                                au fil des filtres. */}
                            {campagneCommencee && (
                              <>
                                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                                  Origine
                                </th>
                                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                                  Mise à jour
                                </th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {/* ⚠️ AUCUN `onClick` SUR LA LIGNE, et aucun bouton
                              d'ouverture de fiche : décision de Daniel du
                              3 octobre 2026 sur l'écran des badges, reprise
                              ici. Ne pas réintroduire. */}
                          {listeAffichee.map((eleve) => (
                            <tr key={eleve.id} className="transition-colors hover:bg-gray-50/70">
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-2.5">
                                  <div
                                    className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                                    style={{ backgroundColor: eleve.a_photo ? ACCENT : '#9ca3af' }}
                                  />
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                      {eleve.nom} {eleve.prenom}
                                    </p>
                                    {eleve.matricule ? (
                                      <p className="font-mono text-[11px] text-gray-500">
                                        {eleve.matricule}
                                      </p>
                                    ) : (
                                      <p className="text-[11px] text-gray-500">
                                        Matricule non renseigné
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3.5">
                                {eleve.classe ? (
                                  <span className="text-xs text-gray-600">{eleve.classe}</span>
                                ) : (
                                  <span className="text-xs text-gray-500">Non affecté</span>
                                )}
                              </td>

                              <td className="px-4 py-3.5">
                                <EtatPhoto aPhoto={Boolean(eleve.a_photo)} />
                              </td>

                              {campagneCommencee && (
                                <>
                                  <td className="px-4 py-3.5">
                                    <OriginePhoto
                                      aPhoto={Boolean(eleve.a_photo)}
                                      source={eleve.source}
                                    />
                                  </td>
                                  <td className="px-4 py-3.5">
                                    {eleve.a_photo ? (
                                      <DateMiseAJour valeur={eleve.maj_le} />
                                    ) : (
                                      <span className="text-xs text-gray-500">
                                        <span aria-hidden="true">—</span>
                                        <span className="sr-only">Sans objet, aucune photo</span>
                                      </span>
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Le plafond se dit, avec le compte exact : une liste
                        tronquée en silence ferait croire la classe plus petite
                        qu'elle n'est, et c'est sur ce chiffre qu'on planifie. */}
                    {!toutAfficher && listeEleves.length > PLAFOND_LIGNES && (
                      <div className="flex flex-wrap items-center gap-3 bg-gray-50/60 px-5 py-3.5">
                        <p className="text-xs text-gray-600">
                          {PLAFOND_LIGNES} élèves affichés sur{' '}
                          <strong className="font-semibold text-gray-700">
                            {listeEleves.length}
                          </strong>{' '}
                          — filtrez par classe ou cherchez un nom pour aller plus vite.
                        </p>
                        <button
                          type="button"
                          onClick={() => setToutAfficher(true)}
                          className="ml-auto rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-all hover:border-gray-300 hover:bg-gray-50"
                        >
                          Afficher les {listeEleves.length} élèves
                        </button>
                      </div>
                    )}
                  </>
                )}
              </CarteReglages>
            )}

            {/* Encadré d'information — norme §8, un seul par section, et EN FIN
                DE PAGE : il explique l'écran, il ne porte aucune donnée. Placé
                entre le total de l'école et le tableau des classes, il
                repoussait les deux chiffres qu'on vient chercher. Sa place est
                sous le tableau dont il commente la colonne « Origine ». */}
            {!erreur && totaux.eleves > 0 && (
              <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
                <div className="flex gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
                    <Info className="h-4 w-4 text-[#246990]" />
                  </div>
                  <div className="text-xs leading-relaxed text-gray-600">
                    <p className="mb-1 text-[13px] font-semibold text-[#246990]">
                      Ce que cette page montre, et ce qu&apos;elle ne fait pas
                    </p>
                    <ul className="space-y-1">
                      <li>
                        • Une photo arrive par deux chemins : l&apos;école la pose depuis la{' '}
                        <strong className="text-gray-700">fiche de l&apos;élève</strong>, ou une
                        famille l&apos;envoie depuis l&apos;application des parents. La colonne
                        « Origine » dit lequel des deux.
                      </li>
                      <li>
                        • L&apos;origine n&apos;est suivie que depuis le 4 octobre 2026 : une photo
                        posée avant cette date s&apos;affiche{' '}
                        <strong className="text-gray-700">« Origine inconnue »</strong>. Ce
                        n&apos;est pas une anomalie, et il n&apos;y a rien à corriger.
                      </li>
                      <li>
                        • <strong className="text-gray-700">Cette page est en lecture seule</strong>{' '}
                        : elle ne permet pas d&apos;envoyer une photo, et elle n&apos;ouvre aucun
                        dossier. Elle sert à savoir où en est la campagne et à sortir les listes.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* État vide réel : l'école n'a aucun élève actif. Distinct de
                l'échec de lecture juste au-dessus, et distinct d'une campagne à
                zéro — ici il n'y a personne à photographier.
                Aucun bouton de navigation : cette page n'ouvre aucun dossier. */}
            {!erreur && totaux.eleves === 0 && (
              <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
                  <Camera className="h-5 w-5 text-gray-500" />
                </div>
                <p className="text-sm font-semibold text-gray-700">
                  Aucun élève actif pour l&apos;année scolaire
                  {anneeScolaire ? ` ${anneeScolaire}` : ' en cours'}
                </p>
                <p className="max-w-md text-xs leading-relaxed text-gray-600">
                  La campagne photo se calcule sur les élèves actifs de l&apos;année active. Dès
                  que des inscriptions sont enregistrées, leurs dossiers apparaissent ici avec
                  leur classe et l&apos;état de leur photo.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotosEleves;
